// iter-97 — device fingerprint helper.
//
// Native (Capacitor) builds get a real per-install UUID via
// ``Device.getId()``. Web falls back to a localStorage-persisted random
// id seeded with a SHA-like hash of the user-agent so the same browser
// produces the same value across page loads.
//
// Stored under ``planflow.device_id`` so any deeper integration (e.g.
// future biometrics) can re-use the same key.

const STORAGE_KEY = "planflow.device_id";
let _cached = null;

function _hashUA(ua) {
    let h = 0;
    for (let i = 0; i < ua.length; i++) {
        h = (h * 31 + ua.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(16);
}

function _genWebId() {
    const rand =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const ua = (navigator && navigator.userAgent) || "";
    return `web_${_hashUA(ua).slice(0, 8)}_${rand}`.slice(0, 96);
}

export async function getDeviceId() {
    if (_cached) return _cached;
    // Try Capacitor first when available (native iOS/Android shell).
    try {
        if (typeof window !== "undefined" && window.Capacitor && window.Capacitor.Plugins?.Device) {
            const info = await window.Capacitor.Plugins.Device.getId();
            if (info && info.identifier) {
                _cached = `cap_${info.identifier}`.slice(0, 96);
                try { localStorage.setItem(STORAGE_KEY, _cached); } catch {}
                return _cached;
            }
        }
    } catch {
        // fall through to web fingerprint
    }
    try {
        const existing = localStorage.getItem(STORAGE_KEY);
        if (existing) {
            _cached = existing;
            return _cached;
        }
    } catch {}
    const fresh = _genWebId();
    try { localStorage.setItem(STORAGE_KEY, fresh); } catch {}
    _cached = fresh;
    return _cached;
}

// Synchronous best-effort accessor for axios interceptors — kicks off
// the async resolver in the background so subsequent calls have a real
// value.
export function getDeviceIdSync() {
    if (_cached) return _cached;
    try {
        const existing = localStorage.getItem(STORAGE_KEY);
        if (existing) {
            _cached = existing;
            return _cached;
        }
    } catch {}
    const fresh = _genWebId();
    try { localStorage.setItem(STORAGE_KEY, fresh); } catch {}
    _cached = fresh;
    // Best-effort upgrade to Capacitor identifier in background.
    getDeviceId().catch(() => {});
    return fresh;
}
